import { useEffect, useMemo, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { api } from '../lib/api'
import { useAuthStore } from '../stores/auth'
import { Card } from '../components/ui/Card'
import { AntetFix } from '../components/ui/AntetFix'

interface Product {
  id: number
  sku: string
  name: string
  brand: string
  category: string
  costProduction: string | null
  vatRate: number
  active: boolean
}

interface PharmaClient {
  id: number
  name: string
  type: string
  target2026: string | null
  active: boolean
}

interface PriceB2C {
  id: number
  productId: number
  channel: string
  listPriceWithVat: string
  salePriceWithVat: string | null
  product: { id: number; sku: string; name: string; brand: string }
}

interface PriceB2B {
  id: number
  productId: number
  clientId: number
  sellInPrice: string
  product: { id: number; sku: string; name: string; brand: string }
  client: { id: number; name: string }
}

interface CommercialDeal {
  id: number
  clientId: number
  offInvoicePct: string | null
  visibilityFeePct: string | null
  commissionPct: string | null
  notes: string | null
  client: { id: number; name: string }
}

interface MarginRow {
  productId: number
  sku: string
  name: string
  brand: string
  costProduction: number | null
  vatRate: number
  channels: {
    type: 'b2c' | 'b2b'
    label: string
    grossPrice: number
    nettRevenue: number
    marginEur: number | null
    marginPct: number | null
    color: 'green' | 'amber' | 'red' | 'gray'
  }[]
}

const B2C_CHANNELS = ['boost4life.ro', 'eMAG'] as const

type Tab = 'b2c' | 'b2b' | 'deals' | 'margins'

const TABS: { value: Tab; label: string }[] = [
  { value: 'b2c', label: 'B2C (canale online)' },
  { value: 'b2b', label: 'B2B Farma' },
  { value: 'deals', label: 'Deal-uri & discount-uri' },
  { value: 'margins', label: 'Margini & P&L' },
]

export function MatricePreturiPage() {
  const [tab, setTab] = useState<Tab>('b2c')

  return (
    <div className="space-y-4">
      <AntetFix>
        <h1 className="text-xl font-semibold">Matrice prețuri & marje</h1>

        <div className="flex border-b border-b4l-border">
          {TABS.map((t) => (
            <button
              key={t.value}
              onClick={() => setTab(t.value)}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition ${
                tab === t.value
                  ? 'border-b4l-green text-b4l-green-dark'
                  : 'border-transparent text-b4l-text-muted hover:text-b4l-text'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </AntetFix>

      {tab === 'b2c' && <B2CTab />}
      {tab === 'b2b' && <B2BTab />}
      {tab === 'deals' && <DealsTab />}
      {tab === 'margins' && <MarginsTab />}
    </div>
  )
}

// ============================================================================
// B2C — products × {boost4life.ro, eMAG}
// ============================================================================

function B2CTab() {
  const queryClient = useQueryClient()
  const permissions = useAuthStore((s) => s.permissions)
  const canWrite = permissions.includes('prices.b2c:write')

  const productsQuery = useQuery<{ products: Product[] }>({
    queryKey: ['products'],
    queryFn: async () => (await api.get<{ products: Product[] }>('/products')).data,
  })
  const pricesQuery = useQuery<{ prices: PriceB2C[] }>({
    queryKey: ['prices', 'b2c'],
    queryFn: async () => (await api.get<{ prices: PriceB2C[] }>('/prices/b2c')).data,
  })

  const setPriceMutation = useMutation({
    mutationFn: async (data: {
      productId: number
      channel: string
      listPriceWithVat: number
      salePriceWithVat?: number | null
    }) => (await api.put('/prices/b2c', data)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['prices', 'b2c'] })
      queryClient.invalidateQueries({ queryKey: ['margins'] })
    },
    onError: showError,
  })

  const products = useMemo(() => productsQuery.data?.products ?? [], [productsQuery.data])
  const priceMap = useMemo(() => {
    const m = new Map<string, PriceB2C>()
    for (const p of pricesQuery.data?.prices ?? []) m.set(`${p.productId}:${p.channel}`, p)
    return m
  }, [pricesQuery.data])

  return (
    <Card description="Prețuri afișate către consumatorul final, cu TVA inclus. Editează inline; salvarea e automată după 500ms.">
      {productsQuery.isLoading ? (
        <p className="text-sm text-b4l-text-muted">Se încarcă...</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-b4l-border text-left text-xs uppercase tracking-wide text-b4l-text-muted">
                <th className="py-2 pr-3 font-medium">SKU</th>
                <th className="py-2 pr-3 font-medium">Denumire</th>
                <th className="py-2 pr-3 font-medium">Brand</th>
                <th className="py-2 pr-3 font-medium text-right">Cost prod.</th>
                <th className="py-2 pr-3 font-medium">TVA</th>
                {B2C_CHANNELS.map((c) => (
                  <th key={c} className="py-2 pr-3 font-medium text-right">
                    {c}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {products
                .filter((p) => p.active)
                .map((p) => (
                  <tr key={p.id} className="border-b border-b4l-border last:border-0">
                    <td className="py-2 pr-3 font-mono text-xs">{p.sku}</td>
                    <td className="py-2 pr-3 max-w-[300px] truncate" title={p.name}>
                      {p.name}
                    </td>
                    <td className="py-2 pr-3 text-xs">{p.brand}</td>
                    <td className="py-2 pr-3 text-right font-mono text-xs">
                      {p.costProduction ? Number(p.costProduction).toFixed(2) : '—'}
                    </td>
                    <td className="py-2 pr-3 text-xs">{p.vatRate}%</td>
                    {B2C_CHANNELS.map((channel) => {
                      const price = priceMap.get(`${p.id}:${channel}`)
                      return (
                        <td key={channel} className="py-2 pr-3">
                          <PriceInput
                            value={price ? Number(price.listPriceWithVat) : null}
                            onCommit={(v) =>
                              v !== null &&
                              setPriceMutation.mutate({
                                productId: p.id,
                                channel,
                                listPriceWithVat: v,
                              })
                            }
                            disabled={!canWrite}
                          />
                        </td>
                      )
                    })}
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  )
}

// ============================================================================
// B2B Farma — products × pharma clients
// ============================================================================

function B2BTab() {
  const queryClient = useQueryClient()
  const permissions = useAuthStore((s) => s.permissions)
  const canWrite = permissions.includes('prices.b2b:write')

  const productsQuery = useQuery<{ products: Product[] }>({
    queryKey: ['products'],
    queryFn: async () => (await api.get<{ products: Product[] }>('/products')).data,
  })
  const clientsQuery = useQuery<{ clients: PharmaClient[] }>({
    queryKey: ['pharma-clients'],
    queryFn: async () => (await api.get<{ clients: PharmaClient[] }>('/pharma-clients')).data,
  })
  const pricesQuery = useQuery<{ prices: PriceB2B[] }>({
    queryKey: ['prices', 'b2b'],
    queryFn: async () => (await api.get<{ prices: PriceB2B[] }>('/prices/b2b')).data,
  })

  const setPriceMutation = useMutation({
    mutationFn: async (data: { productId: number; clientId: number; sellInPrice: number }) =>
      (await api.put('/prices/b2b', data)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['prices', 'b2b'] })
      queryClient.invalidateQueries({ queryKey: ['margins'] })
    },
    onError: showError,
  })

  const products = useMemo(() => productsQuery.data?.products ?? [], [productsQuery.data])
  const clients = useMemo(() => clientsQuery.data?.clients ?? [], [clientsQuery.data])
  const priceMap = useMemo(() => {
    const m = new Map<string, PriceB2B>()
    for (const p of pricesQuery.data?.prices ?? []) m.set(`${p.productId}:${p.clientId}`, p)
    return m
  }, [pricesQuery.data])

  if (clientsQuery.isLoading || productsQuery.isLoading) {
    return <p className="text-sm text-b4l-text-muted">Se încarcă...</p>
  }

  return (
    <Card description="Prețuri sell-in către farmacii (fără TVA). Editează inline; salvarea e automată.">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-b4l-border text-left text-xs uppercase tracking-wide text-b4l-text-muted">
              <th className="py-2 pr-3 font-medium">SKU</th>
              <th className="py-2 pr-3 font-medium">Denumire</th>
              {clients.map((c) => (
                <th key={c.id} className="py-2 pr-3 font-medium text-right">
                  {c.name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {products
              .filter((p) => p.active)
              .map((p) => (
                <tr key={p.id} className="border-b border-b4l-border last:border-0">
                  <td className="py-2 pr-3 font-mono text-xs">{p.sku}</td>
                  <td className="py-2 pr-3 max-w-[260px] truncate" title={p.name}>
                    {p.name}
                  </td>
                  {clients.map((c) => {
                    const price = priceMap.get(`${p.id}:${c.id}`)
                    return (
                      <td key={c.id} className="py-2 pr-3">
                        <PriceInput
                          value={price ? Number(price.sellInPrice) : null}
                          onCommit={(v) =>
                            v !== null &&
                            setPriceMutation.mutate({
                              productId: p.id,
                              clientId: c.id,
                              sellInPrice: v,
                            })
                          }
                          disabled={!canWrite}
                        />
                      </td>
                    )
                  })}
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </Card>
  )
}

// ============================================================================
// Deals — per pharma client
// ============================================================================

function DealsTab() {
  const queryClient = useQueryClient()
  const permissions = useAuthStore((s) => s.permissions)
  const canWrite = permissions.includes('prices.deals:write')

  const clientsQuery = useQuery<{ clients: PharmaClient[] }>({
    queryKey: ['pharma-clients'],
    queryFn: async () => (await api.get<{ clients: PharmaClient[] }>('/pharma-clients')).data,
  })
  const dealsQuery = useQuery<{ deals: CommercialDeal[] }>({
    queryKey: ['deals'],
    queryFn: async () => (await api.get<{ deals: CommercialDeal[] }>('/prices/deals')).data,
  })

  const setDealMutation = useMutation({
    mutationFn: async (data: {
      clientId: number
      offInvoicePct?: number | null
      visibilityFeePct?: number | null
      commissionPct?: number | null
      notes?: string | null
    }) => (await api.put('/prices/deals', data)).data,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['deals'] })
      queryClient.invalidateQueries({ queryKey: ['margins'] })
    },
    onError: showError,
  })

  const dealMap = useMemo(() => {
    const m = new Map<number, CommercialDeal>()
    for (const d of dealsQuery.data?.deals ?? []) m.set(d.clientId, d)
    return m
  }, [dealsQuery.data])

  if (clientsQuery.isLoading) {
    return <p className="text-sm text-b4l-text-muted">Se încarcă...</p>
  }

  const clients = clientsQuery.data?.clients ?? []

  return (
    <Card description="Discount-uri off-invoice, taxă de vizibilitate și comision per client farma. Procente; lasă gol pentru 'nu se aplică'.">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-b4l-border text-left text-xs uppercase tracking-wide text-b4l-text-muted">
              <th className="py-2 pr-3 font-medium">Client</th>
              <th className="py-2 pr-3 font-medium text-right">Off-invoice %</th>
              <th className="py-2 pr-3 font-medium text-right">Vizibilitate %</th>
              <th className="py-2 pr-3 font-medium text-right">Comision %</th>
              <th className="py-2 pr-3 font-medium">Note</th>
            </tr>
          </thead>
          <tbody>
            {clients.map((c) => {
              const d = dealMap.get(c.id)
              return (
                <tr key={c.id} className="border-b border-b4l-border last:border-0">
                  <td className="py-2 pr-3 font-medium">{c.name}</td>
                  <td className="py-2 pr-3">
                    <PriceInput
                      value={d?.offInvoicePct ? Number(d.offInvoicePct) : null}
                      onCommit={(v) => setDealMutation.mutate({ clientId: c.id, offInvoicePct: v })}
                      disabled={!canWrite}
                      max={100}
                    />
                  </td>
                  <td className="py-2 pr-3">
                    <PriceInput
                      value={d?.visibilityFeePct ? Number(d.visibilityFeePct) : null}
                      onCommit={(v) =>
                        setDealMutation.mutate({ clientId: c.id, visibilityFeePct: v })
                      }
                      disabled={!canWrite}
                      max={100}
                    />
                  </td>
                  <td className="py-2 pr-3">
                    <PriceInput
                      value={d?.commissionPct ? Number(d.commissionPct) : null}
                      onCommit={(v) => setDealMutation.mutate({ clientId: c.id, commissionPct: v })}
                      disabled={!canWrite}
                      max={100}
                    />
                  </td>
                  <td className="py-2 pr-3">
                    <NoteInput
                      value={d?.notes ?? ''}
                      onCommit={(v) => setDealMutation.mutate({ clientId: c.id, notes: v || null })}
                      disabled={!canWrite}
                    />
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </Card>
  )
}

// ============================================================================
// Margins — read-only with color coding
// ============================================================================

function MarginsTab() {
  const marginsQuery = useQuery<{ rows: MarginRow[] }>({
    queryKey: ['margins'],
    queryFn: async () => (await api.get<{ rows: MarginRow[] }>('/prices/margins')).data,
  })

  if (marginsQuery.isLoading) {
    return <p className="text-sm text-b4l-text-muted">Se calculează...</p>
  }

  const rows = marginsQuery.data?.rows ?? []
  const productsWithChannels = rows.filter((r) => r.channels.length > 0)

  return (
    <Card description="Marje calculate: nett revenue - cost producție. Cod culoare: verde ≥40%, portocaliu 20-40%, roșu <20%, gri = lipsă cost.">
      {productsWithChannels.length === 0 ? (
        <div className="rounded border border-dashed border-b4l-border p-8 text-center text-sm text-b4l-text-muted">
          Niciun preț setat încă. Completează tab-urile B2C / B2B mai întâi.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-b4l-border text-left text-xs uppercase tracking-wide text-b4l-text-muted">
                <th className="py-2 pr-3 font-medium">SKU</th>
                <th className="py-2 pr-3 font-medium">Produs</th>
                <th className="py-2 pr-3 font-medium">Canal</th>
                <th className="py-2 pr-3 font-medium text-right">Preț brut</th>
                <th className="py-2 pr-3 font-medium text-right">Nett</th>
                <th className="py-2 pr-3 font-medium text-right">Cost</th>
                <th className="py-2 pr-3 font-medium text-right">Margin €</th>
                <th className="py-2 pr-3 font-medium text-right">Margin %</th>
              </tr>
            </thead>
            <tbody>
              {productsWithChannels.flatMap((p) =>
                p.channels.map((ch, idx) => (
                  <tr
                    key={`${p.productId}-${ch.type}-${ch.label}`}
                    className="border-b border-b4l-border last:border-0"
                  >
                    <td className="py-1.5 pr-3 font-mono text-xs">{idx === 0 ? p.sku : ''}</td>
                    <td className="py-1.5 pr-3 text-xs max-w-[260px] truncate" title={p.name}>
                      {idx === 0 ? p.name : ''}
                    </td>
                    <td className="py-1.5 pr-3">
                      <span
                        className={`rounded px-2 py-0.5 text-[10px] ${
                          ch.type === 'b2c'
                            ? 'bg-b4l-blue-light text-b4l-blue-dark'
                            : 'bg-b4l-purple-light text-b4l-purple-dark'
                        }`}
                      >
                        {ch.label}
                      </span>
                    </td>
                    <td className="py-1.5 pr-3 text-right font-mono text-xs">
                      {ch.grossPrice.toFixed(2)}
                    </td>
                    <td className="py-1.5 pr-3 text-right font-mono text-xs text-b4l-text-muted">
                      {ch.nettRevenue.toFixed(2)}
                    </td>
                    <td className="py-1.5 pr-3 text-right font-mono text-xs text-b4l-text-muted">
                      {p.costProduction !== null ? p.costProduction.toFixed(2) : '—'}
                    </td>
                    <td
                      className={`py-1.5 pr-3 text-right font-mono text-xs ${MARGIN_COLOR[ch.color]}`}
                    >
                      {ch.marginEur !== null ? ch.marginEur.toFixed(2) : '—'}
                    </td>
                    <td
                      className={`py-1.5 pr-3 text-right font-mono text-xs font-semibold ${MARGIN_COLOR[ch.color]}`}
                    >
                      {ch.marginPct !== null ? `${ch.marginPct.toFixed(1)}%` : '—'}
                    </td>
                  </tr>
                )),
              )}
            </tbody>
          </table>
          <div className="mt-3 flex flex-wrap gap-3 text-[10px] text-b4l-text-muted">
            <span className="rounded bg-b4l-green-light px-2 py-0.5 text-b4l-green-dark">
              ≥ 40%
            </span>
            <span className="rounded bg-b4l-amber-light px-2 py-0.5 text-b4l-amber-dark">
              20-40%
            </span>
            <span className="rounded bg-b4l-red-light px-2 py-0.5 text-b4l-red-dark">&lt; 20%</span>
            <span className="rounded bg-b4l-bg px-2 py-0.5">cost lipsă</span>
          </div>
        </div>
      )}
    </Card>
  )
}

const MARGIN_COLOR: Record<MarginRow['channels'][0]['color'], string> = {
  green: 'text-b4l-green-dark',
  amber: 'text-b4l-amber-dark',
  red: 'text-b4l-red-dark',
  gray: 'text-b4l-text-dim',
}

// ============================================================================
// Inputs - inline editable with debounced save
// ============================================================================

interface PriceInputProps {
  value: number | null
  onCommit: (val: number | null) => void
  disabled?: boolean
  max?: number
}

function PriceInput({ value, onCommit, disabled, max }: PriceInputProps) {
  const [local, setLocal] = useState(value !== null ? String(value) : '')

  // Sync from server when query refetches.
  useEffect(() => {
    setLocal(value !== null ? String(value) : '')
  }, [value])

  const commit = () => {
    const trimmed = local.trim().replace(',', '.')
    if (!trimmed) {
      // empty -> commit null only if previously had value
      if (value !== null) onCommit(null)
      return
    }
    const n = Number(trimmed)
    if (!Number.isFinite(n) || n < 0 || (max !== undefined && n > max)) {
      toast.error('Valoare invalidă')
      setLocal(value !== null ? String(value) : '')
      return
    }
    if (n !== value) onCommit(n)
  }

  return (
    <input
      type="text"
      inputMode="decimal"
      value={local}
      onChange={(e) => setLocal(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => {
        if (e.key === 'Enter') (e.target as HTMLInputElement).blur()
        if (e.key === 'Escape') {
          setLocal(value !== null ? String(value) : '')
          ;(e.target as HTMLInputElement).blur()
        }
      }}
      disabled={disabled}
      placeholder="—"
      className="w-24 rounded border border-b4l-border bg-white px-2 py-1 text-right font-mono text-xs outline-none focus:border-b4l-green disabled:bg-b4l-bg disabled:text-b4l-text-muted"
    />
  )
}

interface NoteInputProps {
  value: string
  onCommit: (val: string) => void
  disabled?: boolean
}

function NoteInput({ value, onCommit, disabled }: NoteInputProps) {
  const [local, setLocal] = useState(value)
  useEffect(() => {
    setLocal(value)
  }, [value])

  return (
    <input
      type="text"
      value={local}
      onChange={(e) => setLocal(e.target.value)}
      onBlur={() => local !== value && onCommit(local)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') (e.target as HTMLInputElement).blur()
      }}
      disabled={disabled}
      placeholder="—"
      className="w-full min-w-[180px] rounded border border-b4l-border bg-white px-2 py-1 text-xs outline-none focus:border-b4l-green disabled:bg-b4l-bg disabled:text-b4l-text-muted"
    />
  )
}

function showError(err: unknown) {
  const message =
    (err as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error
      ?.message ?? 'Eroare'
  toast.error(message)
}
